import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth.jsx";
import { useTerm } from "@/lib/terminology.jsx";
import { formatDay } from "@/lib/format";
import {
  CheckCircle2, Clock, Edit3, Trash2, Save,
  ExternalLink, History, User, AlertTriangle,
} from "lucide-react";
import TaskModal from "@/components/TaskModal";
import DealMultiPicker from "@/components/DealMultiPicker";
import DealChoicePopover from "@/components/DealChoicePopover";

/**
 * v5.13-r2 TaskDetailPanel — slide-over shown from any task list.
 *
 * Complete/Snooze here follow the same rule as the Tasks page:
 *   0 or 1 open link → confirm popover
 *   ≥2 open links   → checkbox picker
 * Except when `contextDealId` is passed (viewer opened this from inside a
 * specific deal's Tasks tab): then actions always target that deal only.
 *
 * Props:
 *  - taskId: string
 *  - contextDealId?: string
 *  - onClose(): void
 *  - onChanged?(): void
 */
export default function TaskDetailPanel({
  taskId,
  contextDealId = null,
  onClose,
  onChanged,
}) {
  const { user } = useAuth();
  const term = useTerm();
  const [task, setTask] = useState(null);
  const [activity, setActivity] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [editing, setEditing] = useState(false);

  // Snooze
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [snoozeUntil, setSnoozeUntil] = useState("");
  // Complete
  const [completeOpen, setCompleteOpen] = useState(false);
  const [createFollowup, setCreateFollowup] = useState(false);
  const [followupPrefill, setFollowupPrefill] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const load = useCallback(async () => {
    const params = contextDealId ? { context_deal_id: contextDealId } : {};
    const { data } = await api.get(`/v513/tasks/${taskId}`, { params });
    setTask(data);
    const { data: acts } = await api.get(`/v513/tasks/${taskId}/activity`);
    setActivity(acts);
  }, [taskId, contextDealId]);

  useEffect(() => {
    api.get("/team-members").then(({ data }) => setTeamMembers(data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openLinks = (task?.linked_deals || []).filter(
    (d) => d.status !== "completed"
  );

  // ---- Snooze ----
  const doSnoozeWithIds = async (dealIds) => {
    if (!snoozeUntil) {
      toast.error("Choose a date");
      return;
    }
    try {
      const payload = { snooze_until: snoozeUntil };
      if ((task?.linked_deals || []).length > 0) payload.deal_ids = dealIds;
      await api.post(`/v513/tasks/${taskId}/snooze`, payload);
      toast.success(`${term("Task")} snoozed`);
      setSnoozeOpen(false);
      setSnoozeUntil("");
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const onSnoozeConfirm = () => {
    if (contextDealId) return doSnoozeWithIds([contextDealId]);
    if (openLinks.length === 0) return doSnoozeWithIds([]);
    if (openLinks.length === 1) return doSnoozeWithIds([openLinks[0].deal_id]);
    // 2+ handled by DealChoicePopover via its own confirm handler
  };

  // ---- Complete ----
  const doCompleteWithIds = async (dealIds) => {
    try {
      const payload = { create_followup: createFollowup };
      if ((task?.linked_deals || []).length > 0) payload.deal_ids = dealIds;
      const { data } = await api.post(`/v513/tasks/${taskId}/complete`, payload);
      toast.success(`${term("Task")} completed`);
      setCompleteOpen(false);
      onChanged?.();
      if (createFollowup && data?.follow_up_prefill) {
        setFollowupPrefill(data.follow_up_prefill);
      } else {
        await load();
      }
      setCreateFollowup(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const onCompleteConfirm = () => {
    if (contextDealId) return doCompleteWithIds([contextDealId]);
    if (openLinks.length === 0) return doCompleteWithIds([]);
    if (openLinks.length === 1) return doCompleteWithIds([openLinks[0].deal_id]);
    // 2+ handled by DealChoicePopover
  };

  const doDelete = async () => {
    try {
      await api.delete(`/v513/tasks/${taskId}`);
      toast.success("Deleted");
      onChanged?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  if (!task) {
    return (
      <div
        className="fixed inset-0 bg-black/40 z-50 flex justify-end"
        data-testid="task-detail-panel"
      >
        <div className="w-full max-w-xl bg-white h-full p-6 text-sm text-[var(--cw-muted)]">
          Loading…
        </div>
      </div>
    );
  }

  const currentIsOpenHere = contextDealId
    ? (task.context_status || "open") !== "completed"
    : (task.rollup_status || task.status || "open") !== "completed";
  const showChoicePopoverForSnooze =
    snoozeOpen && !contextDealId && openLinks.length >= 2;
  const showChoicePopoverForComplete =
    completeOpen && !contextDealId && openLinks.length >= 2;
  const showSimpleSnooze = snoozeOpen && !showChoicePopoverForSnooze;
  const showSimpleComplete = completeOpen && !showChoicePopoverForComplete;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 z-50 flex justify-end"
        data-testid="task-detail-panel"
        onClick={onClose}
      >
        <div
          className="w-full max-w-xl bg-white h-full flex flex-col shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="px-5 py-4 border-b flex items-start justify-between gap-4"
            style={{ borderColor: "var(--cw-border)" }}
          >
            <div className="flex-1 min-w-0">
              <div className="cw-caption uppercase tracking-widest">
                {term("Task")}
              </div>
              <h2 className="cw-h2 mt-1 truncate" data-testid="task-detail-name">
                {task.task_name}
              </h2>
              {task.duplicated_from_task_id && (
                <div
                  className="mt-1 text-xs italic text-[var(--cw-muted)]"
                  data-testid="task-detail-duplicated-from"
                >
                  Created as a follow-up from “{task.duplicated_from_name || "…"}”
                </div>
              )}
              {contextDealId && (
                <div
                  className="mt-1 text-[11px] uppercase tracking-widest"
                  style={{ color: "var(--cw-primary)" }}
                >
                  Viewing per-{term("deal").toLowerCase()} status
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-[var(--cw-muted)] text-xl"
              data-testid="task-detail-close"
            >
              ×
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            <div
              className="px-5 py-3 border-b flex flex-wrap gap-2"
              style={{ borderColor: "var(--cw-border)" }}
            >
              {currentIsOpenHere ? (
                <>
                  <button
                    onClick={() => setCompleteOpen(true)}
                    className="cw-btn-primary text-sm inline-flex items-center gap-1"
                    data-testid="task-detail-complete-btn"
                  >
                    <CheckCircle2 size={14} /> Complete
                  </button>
                  <button
                    onClick={() => setSnoozeOpen(true)}
                    className="text-sm inline-flex items-center gap-1 px-3 py-1.5 rounded border"
                    style={{ borderColor: "var(--cw-border)" }}
                    data-testid="task-detail-snooze-btn"
                  >
                    <Clock size={14} /> Snooze
                  </button>
                  <button
                    onClick={() => setEditing(!editing)}
                    className="text-sm inline-flex items-center gap-1 px-3 py-1.5 rounded border"
                    style={{ borderColor: "var(--cw-border)" }}
                    data-testid="task-detail-edit-btn"
                  >
                    <Edit3 size={14} /> {editing ? "Done editing" : "Edit"}
                  </button>
                </>
              ) : (
                <div className="text-xs text-[var(--cw-muted)] py-1">
                  {contextDealId
                    ? `Completed for this ${term("deal").toLowerCase()} — permanent. Every other linked ${term("deal").toLowerCase()} is unaffected.`
                    : `Completed on ${formatDay(task.completed_at)} — this ${term("task").toLowerCase()} is permanent.`}
                </div>
              )}
              {(user.role === "vc_admin" || task.created_by_id === user.user_id) && (
                <button
                  onClick={() => setDeleteOpen(true)}
                  className="text-sm inline-flex items-center gap-1 px-3 py-1.5 rounded border ml-auto"
                  style={{ borderColor: "var(--cw-error)", color: "var(--cw-error)" }}
                  data-testid="task-detail-delete-btn"
                >
                  <Trash2 size={14} /> Delete
                </button>
              )}
            </div>

            <div className="p-5 space-y-3">
              {editing && currentIsOpenHere ? (
                <EditFields
                  task={task}
                  teamMembers={teamMembers}
                  onSaved={async () => {
                    await load();
                    onChanged?.();
                  }}
                />
              ) : (
                <ReadFields task={task} contextDealId={contextDealId} />
              )}
            </div>

            <div className="px-5 pb-8">
              <div className="cw-caption uppercase tracking-widest mb-2 flex items-center gap-1">
                <History size={11} /> Full history
              </div>
              <div className="space-y-2" data-testid="task-detail-activity">
                {activity.length === 0 && (
                  <div className="text-sm text-[var(--cw-muted)]">
                    No events recorded yet.
                  </div>
                )}
                {activity.map((a) => (
                  <ActivityRow key={a.log_id} a={a} linkedDeals={task.linked_deals} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Snooze — simple confirm */}
      {showSimpleSnooze && (
        <SmallPopover
          onClose={() => setSnoozeOpen(false)}
          title={`Snooze ${term("task").toLowerCase()}`}
          description={
            contextDealId
              ? `Only this ${term("deal").toLowerCase()}'s copy will be snoozed.`
              : openLinks.length === 1
                ? `Only ${openLinks[0].company_name}'s copy will be snoozed.`
                : undefined
          }
          testid="task-snooze-popover"
        >
          <div className="text-xs cw-caption uppercase">Snooze until</div>
          <input
            type="date"
            value={snoozeUntil}
            onChange={(e) => setSnoozeUntil(e.target.value)}
            className="w-full h-9 px-3 rounded-md border text-sm mt-1"
            style={{ borderColor: "var(--cw-border)" }}
            data-testid="task-snooze-date"
          />
          <div className="mt-3 flex justify-end gap-2">
            <button onClick={() => setSnoozeOpen(false)} className="cw-btn-ghost text-sm">
              Cancel
            </button>
            <button
              disabled={!snoozeUntil}
              onClick={onSnoozeConfirm}
              className={`cw-btn-primary text-sm ${!snoozeUntil ? "opacity-40" : ""}`}
              data-testid="task-snooze-confirm"
            >
              Confirm
            </button>
          </div>
        </SmallPopover>
      )}

      {/* Snooze — checkbox picker */}
      {showChoicePopoverForSnooze && (
        <DealChoicePopover
          open
          onClose={() => setSnoozeOpen(false)}
          onConfirm={(ids) => doSnoozeWithIds(ids)}
          title={`Snooze '${task.task_name}'`}
          description={`Choose which linked ${term("deals").toLowerCase()} to snooze. Every other linked ${term("deal").toLowerCase()} stays exactly as it is.`}
          actionLabel="Snooze"
          openLinkedDeals={openLinks}
          extra={
            <div className="mt-3">
              <div className="cw-caption uppercase text-xs">Snooze until</div>
              <input
                type="date"
                value={snoozeUntil}
                onChange={(e) => setSnoozeUntil(e.target.value)}
                className="w-full h-9 px-3 rounded-md border text-sm mt-1"
                style={{ borderColor: "var(--cw-border)" }}
                data-testid="task-detail-snooze-choice-date"
              />
            </div>
          }
          testid="task-detail-snooze-choice"
        />
      )}

      {/* Complete — simple confirm */}
      {showSimpleComplete && (
        <SmallPopover
          onClose={() => setCompleteOpen(false)}
          title={`Mark '${task.task_name}' as complete?`}
          description={
            contextDealId
              ? `Only this ${term("deal").toLowerCase()} will be marked complete.`
              : openLinks.length === 1
                ? `Only ${openLinks[0].company_name} will be marked complete.`
                : undefined
          }
          testid="task-complete-popover"
        >
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={createFollowup}
              onChange={(e) => setCreateFollowup(e.target.checked)}
              data-testid="task-complete-followup-checkbox"
            />
            Create a follow-up {term("task").toLowerCase()}
          </label>
          <div className="text-xs text-[var(--cw-muted)] mt-2">
            Completing is permanent — this record won't reopen.
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <button
              onClick={() => setCompleteOpen(false)}
              className="cw-btn-ghost text-sm"
            >
              Cancel
            </button>
            <button
              onClick={onCompleteConfirm}
              className="cw-btn-primary text-sm"
              data-testid="task-complete-confirm"
            >
              Confirm
            </button>
          </div>
        </SmallPopover>
      )}

      {/* Complete — checkbox picker */}
      {showChoicePopoverForComplete && (
        <DealChoicePopover
          open
          onClose={() => setCompleteOpen(false)}
          onConfirm={(ids) => doCompleteWithIds(ids)}
          title={`Mark '${task.task_name}' complete`}
          description={`Completing is permanent for the chosen ${term("deals").toLowerCase()}. Every other linked ${term("deal").toLowerCase()} stays open.`}
          actionLabel="Complete"
          openLinkedDeals={openLinks}
          extra={
            <label className="mt-3 flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={createFollowup}
                onChange={(e) => setCreateFollowup(e.target.checked)}
                data-testid="task-detail-complete-choice-followup"
              />
              Create a follow-up {term("task").toLowerCase()}
            </label>
          }
          testid="task-detail-complete-choice"
        />
      )}

      {deleteOpen && (
        <SmallPopover
          onClose={() => setDeleteOpen(false)}
          title={`Delete '${task.task_name}'?`}
          testid="task-delete-popover"
        >
          <div className="text-xs text-[var(--cw-muted)] flex items-start gap-1">
            <AlertTriangle size={12} className="mt-0.5" />
            This removes the {term("task").toLowerCase()} and logs a deletion event
            on every linked {term("deal").toLowerCase()}'s Flow Log.
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <button onClick={() => setDeleteOpen(false)} className="cw-btn-ghost text-sm">
              Cancel
            </button>
            <button
              onClick={doDelete}
              className="text-sm px-3 py-1.5 rounded"
              style={{ background: "var(--cw-error)", color: "white" }}
              data-testid="task-delete-confirm"
            >
              Delete
            </button>
          </div>
        </SmallPopover>
      )}

      {followupPrefill && (
        <TaskModal
          prefill={followupPrefill}
          onClose={() => {
            setFollowupPrefill(null);
            load();
          }}
          onCreated={async (newId) => {
            try {
              await api.post(`/v513/tasks/${taskId}/log-followup`, {
                new_task_id: newId,
              });
            } catch {
              /* non-fatal */
            }
            setFollowupPrefill(null);
            onChanged?.();
            load();
          }}
          title={`Follow-up ${term("Task")}`}
          submitLabel={`Create ${term("Task")}`}
        />
      )}
    </>
  );
}

// ---------- helpers ----------
function SmallPopover({ onClose, title, description, children, testid }) {
  return (
    <div
      className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4"
      data-testid={testid}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl w-full max-w-sm p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="cw-h2 text-base">{title}</h3>
        {description && (
          <div className="text-xs text-[var(--cw-muted)] mt-1">{description}</div>
        )}
        <div className="mt-3">{children}</div>
      </div>
    </div>
  );
}

function ReadFields({ task, contextDealId }) {
  const term = useTerm();
  const rollup = task.rollup_status || task.status || "open";
  const badge = (() => {
    if (contextDealId) {
      const cs = task.context_status || "open";
      return cs === "completed"
        ? { bg: "rgba(44,154,82,0.15)", fg: "#1F6E3A", text: "Completed here" }
        : { bg: "rgba(40,42,116,0.08)", fg: "var(--cw-primary)", text: "Open here" };
    }
    if (rollup === "completed")
      return { bg: "rgba(44,154,82,0.15)", fg: "#1F6E3A", text: "Completed" };
    if (rollup === "partially_completed")
      return { bg: "rgba(243,191,92,0.25)", fg: "#8A6510", text: "Partially completed" };
    return { bg: "rgba(40,42,116,0.08)", fg: "var(--cw-primary)", text: "Open" };
  })();

  return (
    <>
      <Row label="Status">
        <span
          className="text-xs font-semibold px-2 py-0.5 rounded"
          style={{ background: badge.bg, color: badge.fg }}
          data-testid="task-detail-status-badge"
        >
          {badge.text}
        </span>
      </Row>
      <Row label="Description">
        {task.description || <span className="text-[var(--cw-muted)]">—</span>}
      </Row>
      <Row label="Assigned to">{task.assigned_to_name || "—"}</Row>
      <Row label={contextDealId ? "Due date (this deal)" : "Default due date"}>
        {task.effective_due_date || task.due_date || "—"}
      </Row>
      <Row label="Priority">{task.priority?.toUpperCase() || "—"}</Row>
      <Row label="Reminder">
        {task.reminder_enabled ? `Yes — ${task.remind_on}` : "No"}
      </Row>
      <Row label="Reviewer">
        {task.reviewer_enabled ? task.reviewer_name || "—" : "None"}
      </Row>
      <Row label={`Linked ${term("Deals")}`}>
        <div className="space-y-1">
          {(task.linked_deals || []).length === 0 && (
            <span className="text-[var(--cw-muted)]">—</span>
          )}
          {(task.linked_deals || []).map((d) => (
            <div
              key={d.deal_id}
              className="flex items-center gap-2 text-xs"
              data-testid={`task-detail-linked-deal-${d.deal_id}`}
            >
              <Link
                to={`/deals/${d.deal_id}`}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded"
                style={{
                  background: "rgba(40,42,116,0.08)",
                  color: "var(--cw-primary)",
                }}
              >
                {d.company_name} <ExternalLink size={10} />
              </Link>
              <span
                className="text-[10px] font-semibold px-1 py-0.5 rounded"
                style={
                  d.status === "completed"
                    ? { background: "rgba(44,154,82,0.15)", color: "#1F6E3A" }
                    : { background: "rgba(40,42,116,0.05)", color: "var(--cw-muted)" }
                }
              >
                {d.status === "completed" ? "COMPLETED" : "OPEN"}
              </span>
              <span className="text-[var(--cw-muted)]">
                Due {d.due_date || "—"}
                {d.due_snoozed && " (snoozed)"}
              </span>
            </div>
          ))}
        </div>
      </Row>
    </>
  );
}

function EditFields({ task, teamMembers, onSaved }) {
  const term = useTerm();
  const [form, setForm] = useState({
    task_name: task.task_name,
    description: task.description || "",
    assigned_to_id: task.assigned_to_id,
    due_date: task.due_date,
    priority: task.priority,
    reminder_enabled: !!task.reminder_enabled,
    remind_on: task.remind_on || "",
    reviewer_enabled: !!task.reviewer_enabled,
    reviewer_id: task.reviewer_id || "",
  });
  const [selectedDeals, setSelectedDeals] = useState(task.linked_deals || []);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!form.reminder_enabled) payload.remind_on = null;
      if (!form.reviewer_enabled) payload.reviewer_id = null;
      payload.deal_ids = selectedDeals.map((d) => d.deal_id);
      await api.patch(`/v513/tasks/${task.task_id}`, payload);
      toast.success("Saved");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const inp = "w-full h-9 px-3 rounded-md border text-sm mt-1";
  const border = { borderColor: "var(--cw-border)" };

  return (
    <div className="space-y-3">
      <F label={`${term("Task")} name`}>
        <input
          value={form.task_name}
          onChange={(e) => setForm({ ...form, task_name: e.target.value })}
          className={inp}
          style={border}
          data-testid="task-edit-name"
        />
      </F>
      <F label="Description">
        <textarea
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          className={inp + " min-h-[70px] py-2"}
          style={border}
        />
      </F>
      <div className="grid grid-cols-2 gap-3">
        <F label="Assign to">
          <select
            value={form.assigned_to_id}
            onChange={(e) => setForm({ ...form, assigned_to_id: e.target.value })}
            className={inp}
            style={border}
            data-testid="task-edit-assignee"
          >
            {teamMembers.map((m) => (
              <option key={m.user_id} value={m.user_id}>
                {m.full_name || m.email}
              </option>
            ))}
          </select>
        </F>
        <F label="Default due date">
          <input
            type="date"
            value={form.due_date}
            onChange={(e) => setForm({ ...form, due_date: e.target.value })}
            className={inp}
            style={border}
            data-testid="task-edit-due"
          />
        </F>
        <F label="Priority">
          <select
            value={form.priority}
            onChange={(e) => setForm({ ...form, priority: e.target.value })}
            className={inp}
            style={border}
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </F>
      </div>

      <F label={`Related ${term("Deals")}`}>
        <DealMultiPicker
          selected={selectedDeals}
          onChange={setSelectedDeals}
          testidPrefix="task-edit-related-deals"
        />
      </F>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={form.reminder_enabled}
          onChange={(e) => setForm({ ...form, reminder_enabled: e.target.checked })}
        />
        Send a reminder
      </label>
      {form.reminder_enabled && (
        <F label="Remind on">
          <input
            type="date"
            value={form.remind_on || ""}
            onChange={(e) => setForm({ ...form, remind_on: e.target.value })}
            className={inp}
            style={border}
          />
        </F>
      )}

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={form.reviewer_enabled}
          onChange={(e) => setForm({ ...form, reviewer_enabled: e.target.checked })}
        />
        Set a reviewer
      </label>
      {form.reviewer_enabled && (
        <F label="Reviewer">
          <select
            value={form.reviewer_id || ""}
            onChange={(e) => setForm({ ...form, reviewer_id: e.target.value })}
            className={inp}
            style={border}
          >
            <option value="">Select…</option>
            {teamMembers.map((m) => (
              <option key={m.user_id} value={m.user_id}>
                {m.full_name || m.email}
              </option>
            ))}
          </select>
        </F>
      )}

      <div className="pt-2 flex justify-end">
        <button
          disabled={saving}
          onClick={save}
          className="cw-btn-primary text-sm inline-flex items-center gap-1"
          data-testid="task-edit-save"
        >
          <Save size={13} /> {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="text-sm flex items-start gap-2">
      <div className="w-40 shrink-0 cw-caption uppercase tracking-widest pt-0.5">
        {label}
      </div>
      <div className="flex-1">{children}</div>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <label className="cw-caption uppercase tracking-widest">{label}</label>
      {children}
    </div>
  );
}

function ActivityRow({ a, linkedDeals }) {
  const detail = a.event_detail || {};
  const dealName = detail.deal_id
    ? (linkedDeals || []).find((d) => d.deal_id === detail.deal_id)?.company_name
    : null;
  let text = "Event";
  switch (a.event_type) {
    case "created":
      text = `Created${detail.linked_deal_count ? ` (linked to ${detail.linked_deal_count} deal${detail.linked_deal_count === 1 ? "" : "s"})` : ""}`;
      break;
    case "field_edited":
      text = `${detail.field} changed from "${fmtVal(detail.old_value)}" to "${fmtVal(detail.new_value)}"`;
      break;
    case "snoozed":
      text = dealName
        ? `Snoozed for ${dealName} — due date moved to ${detail.new_due_date}`
        : `Snoozed — due date moved to ${detail.new_due_date}`;
      break;
    case "completed":
      text = dealName
        ? `Marked complete for ${dealName}`
        : "Marked complete";
      break;
    case "followup_created":
      text = `Follow-up task created: "${detail.new_task_name || detail.new_task_id}"`;
      break;
    case "deleted":
      text = "Deleted";
      break;
    case "deal_linked":
      text = `Linked to a deal`;
      break;
    case "deal_unlinked":
      text = `Unlinked from a deal`;
      break;
    case "reviewer_notified":
      text = detail.deal_ids && detail.deal_ids.length
        ? `Reviewer notified for ${detail.deal_ids.length} deal${detail.deal_ids.length === 1 ? "" : "s"}`
        : "Reviewer notified";
      break;
    default:
      text = a.event_type;
  }
  return (
    <div
      className="cw-card p-3 flex items-start gap-3"
      data-testid={`task-activity-${a.event_type}`}
    >
      <div
        className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
        style={{ background: "rgba(40,42,116,0.08)", color: "var(--cw-primary)" }}
      >
        <User size={12} />
      </div>
      <div className="flex-1">
        <div className="text-sm">{text}</div>
        <div className="cw-caption mt-1">
          {a.actor_name || "System"} · {formatDay(a.created_at)}
        </div>
      </div>
    </div>
  );
}

function fmtVal(v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v);
}
